import { defineMessage } from '@lingui/macro'

const globalDefinitions = {
  greeting: defineMessage({
    id: 'globals.greeting',
    message: 'Hallo {value}',
  }),
  required: defineMessage({
    id: 'globals.validation.required',
    comment: 'Generic validation message when a field is required.',
    message: 'Dit veld is verplicht.',
  }),
  commentRequired: defineMessage({
    id: 'globals.validation.comment-required',
    comment: 'Validation message when a selected option requires a free-text comment.',
    message: 'Specifieer jouw antwoord.',
  }),
  positiveValue: defineMessage({
    id: 'globals.validation.positive-value',
    comment: 'Generic validation message when a value is not positive.',
    message: 'Voor dit veld mag enkel een positieve waarde ingevuld worden.',
  }),
  minValue: defineMessage({
    id: 'globals.validation.minimum-value',
    comment: 'Generic validation message when a value is below the minimum.',
    message: 'Dit veld moet groter of gelijk zijn aan {value}.',
  }),
  maxValue: defineMessage({
    id: 'globals.validation.maximum-value',
    comment: 'Generic validation message when a value is above the maximum.',
    message: 'Dit veld moet kleiner of gelijk zijn aan {value}.',
  }),

  minLength: defineMessage({
    id: 'globals.validation.minimum-length',
    comment: 'Generic validation message when a string value is below the required length.',
    message: 'Dit veld moet minimaal {value} karakters bevatten.',
  }),
  maxLength: defineMessage({
    id: 'globals.validation.maximum-length',
    comment: 'Generic validation message when a string value is above the allowed length.',
    message: 'Dit veld mag maximaal {value} karakters bevatten.',
  }),

  // Phone validation
  invalidPhoneFixed: defineMessage({
    id: 'globals.validation.phone-number-invalid-format-fixed',
    comment: 'Generic validation message when a fixed phone number is incorrectly formatted.',
    message:
      'Ongeldig telefoonnummer. Kijk even na waar het misging. Gebruik daarbij steeds het volgende formaat: +32 X XXX XX XX',
  }),

  invalidPhoneMobile: defineMessage({
    id: 'globals.validation.phone-number-invalid-format-mobile',
    comment: 'Generic validation message when a mobile phone number is incorrectly formatted.',
    message:
      'Ongeldig telefoonnummer. Kijk even na waar het misging. Gebruik daarbij steeds het volgende formaat: +32 XXX XX XX XX',
  }),

  invalidTypeFixed: defineMessage({
    id: 'globals.validation.phone-number-invalid-type-fixed',
    comment: 'Generic validation message when a phone number is not of the fixed type',
    message:
      'Dit telefoonnummer is geen vast telefoonnummer. Kijk even na waar het misging. Gebruik daarbij steeds het volgende formaat: +32 X XXX XX XX',
  }),

  invalidTypeMobile: defineMessage({
    id: 'globals.validation.phone-number-invalid-type-mobile',
    comment: 'Generic validation message when a phone number is not of the mobile type.',
    message:
      'Dit telefoonnummer is geen mobiel telefoonnummer. Kijk even na waar het misging. Gebruik daarbij steeds het volgende formaat: +32 XXX XX XX XX',
  }),

  invalidRegion: defineMessage({
    id: 'globals.validation.phone-number-invalid-region',
    comment: 'Generic validation message when a phone numbers region is incorrect.',
    message:
      'Dit telefoonnummer is niet geldig in combinatie met de gebruikte landcode. Kijk even na waar het misging. Gebruik daarbij steeds het volgende formaat: +32 XXX XX XX XX',
  }),

  invalidEmail: defineMessage({
    id: 'globals.validation.email-address',
    comment: 'Generic validation message when an emailaddress is incorrectly formatted.',
    message: 'Het ingegeven e-mailadres is niet geldig.',
  }),
  goBack: defineMessage({
    id: 'globals.go-back',
    comment: 'Generic go back action label.',
    message: 'Terug',
  }),
  close: defineMessage({
    id: 'globals.close',
    comment: 'Generic close action label.',
    message: 'Sluiten',
  }),
  cancel: defineMessage({
    id: 'globals.cancel',
    comment: 'Generic cancel action label.',
    message: 'Annuleren',
  }),
  remove: defineMessage({
    id: 'globals.remove',
    comment: 'Generic remove action label.',
    message: 'Verwijderen',
  }),

  gender: defineMessage({
    id: 'globals.gender.label',
    comment: 'Gender label.',
    message: 'Geslacht',
  }),

  genderM: defineMessage({
    id: 'globals.gender.male',
    comment: 'Gender label for male.',
    message: 'Man',
  }),

  genderF: defineMessage({
    id: 'globals.gender.female',
    comment: 'Gender label for female',
    message: 'Vrouw',
  }),

  genderU: defineMessage({
    id: 'globals.gender.unknown',
    comment: 'Gender label for unknown.',
    message: 'Onbekend',
  }),

  genderO: defineMessage({
    id: 'globals.gender.other',
    comment: 'Gender label for other',
    message: 'Andere',
  }),

  labelName: defineMessage({
    id: 'globals.labels.name',
    comment: 'Label for name',
    message: 'Naam',
  }),

  labelGivenName: defineMessage({
    id: 'globals.labels.givenName',
    comment: 'Label for given name',
    message: 'Voornaam',
  }),

  labelFamilyName: defineMessage({
    id: 'globals.labels.familyName',
    comment: 'Label for family name',
    message: 'Familienaam',
  }),

  labelDateOfBirth: defineMessage({
    id: 'globals.labels.dateOfBirth',
    comment: 'Label for date of birth',
    message: 'Geboortedatum',
  }),

  profileHealthGeneral: defineMessage({
    id: 'profile.health.general',
    message: 'Algemeen',
  }),
  profileHealthMedication: defineMessage({
    id: 'profile.health.medication',
    message: 'Medicatie',
  }),
  profileHealthAllergies: defineMessage({
    id: 'profile.health.allergies',
    message: 'Allergieën',
  }),

  // Observation code translations
  bothrs_length: defineMessage({
    id: 'profile.health.observation.bothrs_length',
    message: 'Lengte',
  }),
  bothrs_weight: defineMessage({
    id: 'profile.health.observation.bothrs_weight',
    message: 'Gewicht',
  }),
  bothrs_smoking: defineMessage({
    id: 'profile.health.observation.bothrs_smoking',
    message: 'Roken',
  }),
  bothrs_allergy: defineMessage({
    id: 'profile.health.observation.bothrs_allergy',
    message: 'Allergie',
  }),
  medication: defineMessage({
    id: 'profile.health.observation.medication',
    message: 'Medicatieschema',
  }),

  // Health param change activity translations
  change_bothrs_length: defineMessage({
    id: 'timeline.activity.change_bothrs_length',
    message: 'Je wijzigde je lengte',
  }),
  change_bothrs_weight: defineMessage({
    id: 'timeline.activity.change_bothrs_weight',
    message: 'Je wijzigde je gewicht',
  }),
  change_bothrs_smoking: defineMessage({
    id: 'timeline.activity.change_bothrs_smoking',
    message: 'Je wijzigde je rookstatus',
  }),
  change_bothrs_allergy: defineMessage({
    id: 'timeline.activity.change_bothrs_allergy',
    message: 'Je wijzigde je allergieën',
  }),
  change_medication: defineMessage({
    id: 'timeline.activity.change_medication',
    message: 'Je wijzigde je medicatieschema',
  }),

  // Document translations
  document: {
    title: defineMessage({
      id: 'document.title',
      message: 'Documenten',
    }),
    fileType: {
      file: defineMessage({
        id: 'document.fileType.file',
        message: 'Bestand',
      }),
      signableDocument: defineMessage({
        id: 'document.fileType.signableDocument',
        message: 'Document',
      }),
    },
    filters: {
      all: defineMessage({
        id: 'documents.filters.all',
        message: 'Alle',
      }),
      documents: defineMessage({
        id: 'documents.filters.documents',
        message: 'Documenten',
      }),
      brochures: defineMessage({
        id: 'documents.filters.brochures',
        message: 'Brochures',
      }),
      toSign: defineMessage({
        id: 'documents.filters.toSign',
        message: 'Te ondertekenen',
      }),
      signed: defineMessage({
        id: 'documents.filters.signed',
        message: 'Ondertekend',
      }),
    },
  },

  // Message translations
  message: {
    title: defineMessage({
      id: 'message.title',
      message: 'Berichten',
    }),
    unread: defineMessage({
      id: 'message.unread',
      message: 'Ongelezen',
    }),
    read: defineMessage({
      id: 'message.read',
      message: 'Gelezen',
    }),
  },

  // Task translations
  task: {
    title: defineMessage({
      id: 'task.title',
      message: 'Taken',
    }),
    pending: defineMessage({
      id: 'task.pending',
      message: 'Openstaand',
    }),
    resolved: defineMessage({
      id: 'task.resolved',
      message: 'Afgewerkt',
    }),
    repeating: defineMessage({
      id: 'task.repeating',
      message: 'Terugkerende activiteit',
    }),
    recurring: defineMessage({
      id: 'task.recurring',
      message: 'Terugkerend',
    }),
    completed: defineMessage({
      id: 'task.completed',
      message: 'Afgewerkt',
    }),
    no_expiry: defineMessage({
      id: 'task.no_expiry',
      message: 'Sinds {date}',
    }),
    expired: defineMessage({
      id: 'task.expired',
      message: 'Niet tijdig afgewerkt',
    }),
    expires_today: defineMessage({
      id: 'task.expires_today',
      message: 'Vandaag uit te voeren',
    }),
    expires_in_one_day: defineMessage({
      id: 'task.expires_in_one_day',
      message: 'Nog 1 dag',
    }),
    expires_in_x_days: defineMessage({
      id: 'task.expires_in_x_days',
      message: 'Nog {count} dagen',
    }),
    Message: {
      description: defineMessage({
        id: 'task.types.message',
        message: 'Te lezen en goed te keuren bericht',
      }),
      expired: defineMessage({
        id: 'task.types.message.message.expired',
        message: 'Niet tijdig bevestigd',
      }),
      completed: defineMessage({
        id: 'task.types.message.completed',
        message: 'Gelezen en bevestigd op {date}',
      }),
      completed_list: defineMessage({
        id: 'task.types.message.completed_list',
        message: 'Bevestigd op {date}',
      }),
      expires_today: defineMessage({
        id: 'task.types.message.expiry_today',
        message: 'Vandaag te bevestigen',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.message.expiry_one_day',
        message: 'Nog 1 dag om te bevestigen',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.message.expiry_x_days',
        message: 'Nog {count} dagen om te bevestigen',
      }),
      other: defineMessage({
        id: 'task.types.message.expiry_other',
        message: 'Te bevestigen sinds {date}',
      }),
    },
    Document: {
      completed: defineMessage({
        id: 'task.types.document.completed',
        message: 'Ondertekend op {date}',
      }),
      completed_list: defineMessage({
        id: 'task.types.document.completed_list',
        message: 'Ondertekend op {date}',
      }),
      description: defineMessage({
        id: 'task.types.document',
        message: 'Te ondertekenen document',
      }),
      expires_today: defineMessage({
        id: 'task.types.document.expiry_today',
        message: 'Vandaag te ondertekenen',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.document.expiry_one_day',
        message: 'Nog 1 dag om te ondertekenen',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.document.expiry_x_days',
        message: 'Nog {count} dagen om te ondertekenen',
      }),
      other: defineMessage({
        id: 'task.types.document.expiry_other',
        message: 'Te ondertekenen sinds {date}',
      }),
    },
    Questionnaire: {
      description: defineMessage({
        id: 'task.types.questionnaire',
        message: 'Vragenlijst',
      }),
      completed: defineMessage({
        id: 'task.types.questionnaire.completed',
        message: 'Afgewerkt op {date}',
      }),
      completed_list: defineMessage({
        id: 'task.types.questionnaire.completed_list',
        message: 'Afgewerkt op {date}',
      }),
      expires_today: defineMessage({
        id: 'task.types.questionnaire.expiry_today',
        message: 'Vandaag uit te voeren',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.questionnaire.expiry_one_day',
        message: 'Nog 1 dag om in te vullen',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.questionnaire.expiry_x_days',
        message: 'Nog {count} dagen om in te vullen',
      }),
      other: defineMessage({
        id: 'task.types.questionnaire.expiry_other',
        message: 'In te vullen sinds {date}',
      }),
    },
    Remecare: {
      description: defineMessage({
        id: 'task.types.remecare',
        message: 'Remecare',
      }),
      completed: defineMessage({
        id: 'task.types.remecare.completed',
        message: 'Afgewerkt op {date}',
      }),
      expires_today: defineMessage({
        id: 'task.types.remecare.expiry_today',
        message: 'Vandaag uitvoeren',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.remecare.expiry_one_day',
        message: 'Nog 1 dag om uit te voeren',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.remecare.expiry_x_days',
        message: 'Nog {count} dagen om uit te voeren',
      }),
      other: defineMessage({
        id: 'task.types.remecare.expiry_other',
        message: 'Uit te voeren sinds {date}',
      }),
    },
    Hylyght: {
      description: defineMessage({
        id: 'task.types.hylyght',
        message: 'Hylyght',
      }),
      expired_program: defineMessage({
        id: 'task.types.hylyght.expired_program',
        message: 'Afgelopen op {date}',
      }),
      completed_program_description: defineMessage({
        id: 'task.types.hylyght.completed_program_description',
        message:
          'Je hebt het einde van het oefenprogramma bereikt. Je kan steeds de oefeningen in dit programma herbekijken op het Hylyght platform.',
      }),
      completed: defineMessage({
        id: 'task.types.hylyght.completed',
        message: 'Afgewerkt op {date}',
      }),
      expires_today: defineMessage({
        id: 'task.types.hylyght.expiry_today',
        message: 'Vandaag uitvoeren',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.hylyght.expiry_one_day',
        message: 'Nog 1 dag om uit te voeren',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.hylyght.expiry_x_days',
        message: 'Nog {count} dagen om uit te voeren',
      }),
      other: defineMessage({
        id: 'task.types.hylyght.expiry_other',
        message: 'Uit te voeren sinds {date}',
      }),
    },
    Content: {
      description: defineMessage({
        id: 'task.types.content',
        message: 'Artikel',
      }),
      article: {
        completed: defineMessage({
          id: 'task.types.content.article.completed',
          message: 'Gelezen op {date}',
        }),
        expires_today: defineMessage({
          id: 'task.types.content.article.expiry_today',
          message: 'Vandaag lezen',
        }),
        expires_in_one_day: defineMessage({
          id: 'task.types.content.article.expiry_one_day',
          message: 'Nog 1 dag om te lezen',
        }),
        expires_in_x_days: defineMessage({
          id: 'task.types.content.article.expiry_x_days',
          message: 'Nog {count} dagen om te lezen',
        }),
        other: defineMessage({
          id: 'task.types.content.article.expiry_other',
          message: 'Te lezen sinds {date}',
        }),
      },
      video: {
        completed: defineMessage({
          id: 'task.types.content.video.completed',
          message: 'Bekeken op {date}',
        }),
        expires_today: defineMessage({
          id: 'task.types.content.video.expiry_today',
          message: 'Vandaag bekijken',
        }),
        expires_in_one_day: defineMessage({
          id: 'task.types.content.video.expiry_one_day',
          message: 'Nog 1 dag om te bekijken',
        }),
        expires_in_x_days: defineMessage({
          id: 'task.types.content.video.expiry_x_days',
          message: 'Nog {count} dagen om te bekijken',
        }),
        other: defineMessage({
          id: 'task.types.content.video.expiry_other',
          message: 'Te bekijken sinds {date}',
        }),
      },
      audio: {
        completed: defineMessage({
          id: 'task.types.content.audio.completed',
          message: 'Beluisterd op {date}',
        }),
        expires_today: defineMessage({
          id: 'task.types.content.audio.expiry_today',
          message: 'Vandaag beluisteren',
        }),
        expires_in_one_day: defineMessage({
          id: 'task.types.content.audio.expiry_one_day',
          message: 'Nog 1 dag om te beluisteren',
        }),
        expires_in_x_days: defineMessage({
          id: 'task.types.content.audio.expiry_x_days',
          message: 'Nog {count} dagen om te beluisteren',
        }),
        other: defineMessage({
          id: 'task.types.content.audio.expiry_other',
          message: 'Te beluisteren sinds {date}',
        }),
      },
    },
    FileUploadRequest: {
      description: defineMessage({
        id: 'task.types.file-upload-request',
        message: 'Bestanden opladen',
      }),
      completed: defineMessage({
        id: 'task.types.file-upload-request.completed',
        message: 'Opgeladen op {date} om {time}',
      }),
      expires_today: defineMessage({
        id: 'task.types.file-upload-request.expires-today',
        message: 'Vandaag uit te voeren',
      }),
      expires_in_one_day: defineMessage({
        id: 'task.types.file-upload-request.expires-in-one-day',
        message: 'Nog 1 dag om uit te voeren',
      }),
      expires_in_x_days: defineMessage({
        id: 'task.types.file-upload-request.expires-in-x-days',
        message: 'Nog {count} dagen om uit te voeren',
      }),
      pending: defineMessage({
        id: 'task.types.file-upload-request.pending',
        message: 'Aangemaakt op {date}',
      }),
      uploaded_files: defineMessage({
        id: 'file-upload-request.uploaded-files',
        message: 'Opgeladen bestanden',
      }),
      file_unavailable: defineMessage({
        id: 'file-upload-request.file-unavailable',
        message: 'Bestand niet langer beschikbaar',
      }),
      delete_files_note: defineMessage({
        id: 'file-upload-request.delete-files-note',
        message: 'Bestanden laten verwijderen?',
      }),
      delete_files_contact: defineMessage({
        id: 'file-upload-request.delete-files-contact',
        message: 'Contacteer het ziekenhuis',
      }),
      no_email_account: defineMessage({
        id: 'file-upload-request.no-email-account',
        message: 'Geen e-mailaccount gevonden',
      }),
      upload_cta: defineMessage({
        id: 'file-upload-request.upload.cta',
        message: 'Opladen',
      }),
      selection: {
        title: defineMessage({
          id: 'file-upload-request.selection.title',
          message: 'Bestanden opladen',
        }),
        uploading: defineMessage({
          id: 'file-upload-request.selection.uploading',
          message: 'Opladen...',
        }),
        retry: defineMessage({
          id: 'file-upload-request.selection.retry',
          message: 'Opnieuw proberen',
        }),
        submit: defineMessage({
          id: 'file-upload-request.selection.submit',
          message: 'Bestanden opladen',
        }),
        add: defineMessage({
          id: 'file-upload.add',
          message: 'Bestand toevoegen',
        }),
        error: {
          upload_failed: defineMessage({
            id: 'file-upload.error.upload-failed',
            message: 'Upload mislukt, probeer opnieuw',
          }),
          complete_failed: defineMessage({
            id: 'file-upload.error.complete-failed',
            message: 'Er ging iets mis. Probeer opnieuw.',
          }),
          banner_one: defineMessage({
            id: 'file-upload.error.banner.one',
            message: '1 bestand kan niet worden opgeladen.',
          }),
          banner_one_detail: defineMessage({
            id: 'file-upload.error.banner.one-detail',
            message: 'Verwijder het of kies een kleiner bestand.',
          }),
          banner_many: defineMessage({
            id: 'file-upload.error.banner.many',
            message: '{count} bestanden kunnen niet worden opgeladen.',
          }),
          banner_many_detail: defineMessage({
            id: 'file-upload.error.banner.many-detail',
            message: 'Verwijder de bestanden of kies kleinere bestanden.',
          }),
        },
      },
    },
  },
}

export { globalDefinitions }
